import {useMutation, UseMutationOptions, useQueryClient, InfiniteData} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {httpSendConversationMessage, SendMessagePayload} from '../http/conversations.http'
import {ConversationMessage, ConversationMessagesList} from '@/features/conversations/types.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'

export const useSendConversationMessage = (
    options?: UseMutationOptions<ConversationMessage, AxiosError, SendMessagePayload>
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (payload: SendMessagePayload) =>
            parseAxiosPromise({
                axiosPromise: httpSendConversationMessage(payload),
                onZodError: captureException,
                responseShape: ConversationMessage
            }),
        onSuccess: (data, variables, context) => {
            // Optimistically add the new message to the cache
            queryClient.setQueryData(
                [QUERY_KEYS.CONVERSATION_MESSAGES, variables.conversationId, 30],
                (oldData: InfiniteData<ConversationMessagesList> | undefined) => {
                    if (!oldData) return oldData

                    const updatedPages = [...oldData.pages]
                    if (updatedPages.length > 0) {
                        // Add the new message to the last page (most recent messages)
                        const lastPageIndex = updatedPages.length - 1
                        updatedPages[lastPageIndex] = {
                            ...updatedPages[lastPageIndex],
                            data: [data, ...updatedPages[lastPageIndex].data],
                            meta: {
                                ...updatedPages[lastPageIndex].meta,
                                total: updatedPages[lastPageIndex].meta.total + 1
                            }
                        }
                    }

                    return {
                        ...oldData,
                        pages: updatedPages
                    }
                }
            )

            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.CONVERSATIONS, data.sender.id]})

            // Call original onSuccess if provided
            options?.onSuccess?.(data, variables, context)
        },
        ...options
    })
}
